import { getTenant } from '../../../theme/tokens'
import { nowMs } from '../../../shared/lib/relativeDates'
import { makeCompanyName } from '../../../shared/config/localeProfile'
import { BRAND } from '../../../shared/config/brand'

// ── Domain types ─────────────────────────────────────────────────────────

export type PayoutStatus =
  | 'pending'
  | 'submitted'
  | 'partially_submitted'
  | 'failed'
  | 'cancelled'

export type TransferStatus = 'pending' | 'submitted' | 'failed' | 'cancelled'

/**
 * Display status — derived from PayoutStatus.
 *
 * We deliberately don't model an "in transit" / "funds arriving" state.
 * Once a payout is submitted to CIMB we can't reliably observe
 * when the destination account is credited (banks take 1–5 business days
 * and don't notify us), so claiming "in transit" would be making a
 * prediction we can't back up. Every submitted payout shows as 'paid' with
 * a label of "Sent" — i.e. we've handed it off, and the merchant should
 * check their bank for arrival.
 */
export type DisplayStatus =
  | 'paid'
  | 'scheduled'
  | 'partially_submitted'
  | 'failed'
  | 'cancelled'

export interface PayoutTransfer {
  transferId: string
  status: TransferStatus
  grossAmount: number
  totalDeductions: number
  netAmount: number
  bankReference: string
  description: string
}

export interface Payout {
  payoutId: string
  merchantEntity: string
  status: PayoutStatus
  /** Human-readable effective date (e.g. "May 6, 2026 15:00:00 MVT") */
  effectiveDate: string
  /** Parsed effective date for timeline & status derivation */
  effectiveDateRaw: Date
  currency: string
  grossAmount: number
  totalDeductions: number
  netAmount: number
  destinationAccount: string
  transfers: PayoutTransfer[]
}

interface RawTransferEntry {
  merchantEntity: string
  payoutId: string
  payoutStatus: 'Submitted' | 'Pending' | 'Failed'
  payoutEffectiveDate: string
  currency: string
  grossAmount: number
  totalDeductions: number
  netAmount: number
  destinationAccount: string
  description: string
  externalPayoutReference: string
}

// ── Source data ──────────────────────────────────────────────────────────
// All payouts settle into the merchant's CIMB account. Suffix
// matches the bankSuffix mock pattern used elsewhere in the balance feature.

const DESTINATION = `${BRAND.shortCode} •••• 4521`

// Bank references in the raw data carry a legacy "BML-" prefix; surface them
// with the active brand's short code so labels follow the tenant.
function displayBankRef(ref: string): string {
  return ref.replace(/^BML/, BRAND.shortCode)
}

function rawTransfer(
  payoutId: string,
  status: 'Submitted' | 'Pending' | 'Failed',
  effectiveDate: string,
  gross: number,
  deductions: number,
  description: string,
  externalRef: string,
): RawTransferEntry {
  return {
    merchantEntity: makeCompanyName(`${BRAND.name} Merchant`),
    payoutId,
    payoutStatus: status,
    payoutEffectiveDate: effectiveDate,
    currency: getTenant().currency.code,
    grossAmount: gross,
    totalDeductions: deductions,
    netAmount: Math.round((gross - deductions) * 100) / 100,
    destinationAccount: DESTINATION,
    description,
    externalPayoutReference: externalRef,
  }
}

const rawTransfers: RawTransferEntry[] = [
  rawTransfer(
    'up_pending_may6_2026_001',
    'Pending',
    'May 6, 2026 15:00:00 MVT',
    46630.72,
    1399.92,
    'Upcoming payout for 6 May 2026',
    'BML-20260506-TXN-00185',
  ),
  rawTransfer(
    '69a608748649da0d81711cc',
    'Submitted',
    'May 7, 2026 15:00:00 MVT',
    2000000.0,
    58000.0,
    '1 of 3 for 7 May 2026',
    'BML-20260507-TXN-00184',
  ),
  rawTransfer(
    '69a608748649da0d81712dd',
    'Submitted',
    'May 7, 2026 15:00:00 MVT',
    2000000.0,
    58000.0,
    '2 of 3 for 7 May 2026',
    'BML-20260507-TXN-00184',
  ),
  rawTransfer(
    '69a608748649da0d81713ee',
    'Submitted',
    'May 7, 2026 15:00:00 MVT',
    1847300.0,
    53572.7,
    '3 of 3 for 7 May 2026',
    'BML-20260507-TXN-00184',
  ),
  rawTransfer(
    '7a1b2c3d4e5f60718a9b0c1',
    'Submitted',
    'May 6, 2026 15:00:00 MVT',
    1245080.0,
    36107.32,
    '1 of 1 for 6 May 2026',
    'BML-20260506-TXN-00183',
  ),
  rawTransfer(
    '8b2c3d4e5f6a71829c0d1e2',
    'Submitted',
    'May 5, 2026 15:00:00 MVT',
    2000000.0,
    58000.0,
    '1 of 3 for 5 May 2026',
    'BML-20260505-TXN-00179',
  ),
  rawTransfer(
    '8b2c3d4e5f6a71829c0d2f3',
    'Submitted',
    'May 5, 2026 15:00:00 MVT',
    2000000.0,
    58000.0,
    '2 of 3 for 5 May 2026',
    'BML-20260505-TXN-00179',
  ),
  rawTransfer(
    '8b2c3d4e5f6a71829c0d3g4',
    'Submitted',
    'May 5, 2026 15:00:00 MVT',
    210500.0,
    6104.5,
    '3 of 3 for 5 May 2026',
    'BML-20260505-TXN-00179',
  ),
  rawTransfer(
    '9c3d4e5f6g7h81930k1l2m3',
    'Submitted',
    'May 4, 2026 15:00:00 MVT',
    875000.0,
    25375.0,
    '1 of 1 for 4 May 2026',
    'BML-20260504-TXN-00175',
  ),
  rawTransfer(
    '0d4e5f6g7h8i92041l2m3n4',
    'Submitted',
    'May 3, 2026 15:00:00 MVT',
    567120.0,
    16446.48,
    '1 of 1 for 3 May 2026',
    'BML-20260503-TXN-00172',
  ),
  rawTransfer(
    'ae5f6g7h8i9j03152m3n4o5',
    'Submitted',
    'May 2, 2026 15:00:00 MVT',
    1530000.0,
    44370.0,
    '1 of 2 for 2 May 2026',
    'BML-20260502-TXN-00168',
  ),
  rawTransfer(
    'bf6g7h8i9j0k14263n4o5p6',
    'Submitted',
    'May 2, 2026 15:00:00 MVT',
    980500.0,
    28434.5,
    '2 of 2 for 2 May 2026',
    'BML-20260502-TXN-00168',
  ),
  rawTransfer(
    'cg7h8i9j0k1l25374o5p6q7',
    'Submitted',
    'May 1, 2026 15:00:00 MVT',
    2000000.0,
    58000.0,
    '1 of 3 for 1 May 2026',
    'BML-20260501-TXN-00165',
  ),
  rawTransfer(
    'dh8i9j0k1l2m36485p6q7r8',
    'Submitted',
    'May 1, 2026 15:00:00 MVT',
    2000000.0,
    58000.0,
    '2 of 3 for 1 May 2026',
    'BML-20260501-TXN-00165',
  ),
  rawTransfer(
    'ei9j0k1l2m3n47596q7r8s9',
    'Submitted',
    'May 1, 2026 15:00:00 MVT',
    742650.0,
    21536.85,
    '3 of 3 for 1 May 2026',
    'BML-20260501-TXN-00165',
  ),
  rawTransfer(
    'fj0k1l2m3n4o586a7r8s9t0',
    'Submitted',
    'Apr 29, 2026 15:00:00 MVT',
    1125000.0,
    32625.0,
    '1 of 1 for 29 Apr 2026',
    'BML-20260429-TXN-00160',
  ),
  rawTransfer(
    'gk1l2m3n4o5p697b8s9t0u1',
    'Submitted',
    'Apr 27, 2026 15:00:00 MVT',
    1890000.0,
    54810.0,
    '1 of 2 for 27 Apr 2026',
    'BML-20260427-TXN-00155',
  ),
  rawTransfer(
    'hl2m3n4o5p6q708c9t0u1v2',
    'Submitted',
    'Apr 27, 2026 15:00:00 MVT',
    435200.0,
    12620.8,
    '2 of 2 for 27 Apr 2026',
    'BML-20260427-TXN-00155',
  ),
  rawTransfer(
    'im3n4o5p6q7r819d0u1v2w3',
    'Submitted',
    'Apr 25, 2026 15:00:00 MVT',
    650000.0,
    18850.0,
    '1 of 1 for 25 Apr 2026',
    'BML-20260425-TXN-00150',
  ),
  rawTransfer(
    'jn4o5p6q7r8s920e1v2w3x4',
    'Submitted',
    'Apr 23, 2026 15:00:00 MVT',
    2000000.0,
    58000.0,
    '1 of 2 for 23 Apr 2026',
    'BML-20260423-TXN-00145',
  ),
  rawTransfer(
    'ko5p6q7r8s9t031f2w3x4y5',
    'Submitted',
    'Apr 23, 2026 15:00:00 MVT',
    315700.0,
    9155.3,
    '2 of 2 for 23 Apr 2026',
    'BML-20260423-TXN-00145',
  ),
  rawTransfer(
    'lp6q7r8s9t0u142g3x4y5z6',
    'Submitted',
    'Apr 21, 2026 15:00:00 MVT',
    1450000.0,
    42050.0,
    '1 of 1 for 21 Apr 2026',
    'BML-20260421-TXN-00140',
  ),
  rawTransfer(
    'mq7r8s9t0u1v253h4y5z6a7',
    'Submitted',
    'Apr 19, 2026 15:00:00 MVT',
    780000.0,
    22620.0,
    '1 of 1 for 19 Apr 2026',
    'BML-20260419-TXN-00135',
  ),
  rawTransfer(
    'nr8s9t0u1v2w364i5z6a7b8',
    'Submitted',
    'Apr 16, 2026 15:00:00 MVT',
    1680000.0,
    48720.0,
    '1 of 2 for 16 Apr 2026',
    'BML-20260416-TXN-00130',
  ),
  rawTransfer(
    'os9t0u1v2w3x475j6a7b8c9',
    'Submitted',
    'Apr 16, 2026 15:00:00 MVT',
    520370.0,
    15090.73,
    '2 of 2 for 16 Apr 2026',
    'BML-20260416-TXN-00130',
  ),
]

// ── Transform ────────────────────────────────────────────────────────────

function parseEffectiveDate(s: string): Date {
  // "May 6, 2026 15:00:00 MVT" — strip trailing tz, parse rest as UTC and
  // back-shift by +8 (MVT is UTC+8). Output is a Date whose toLocaleString
  // in MVT will read as 15:00 on the given day.
  const cleaned = s.replace(/\s+MVT$/, '')
  const ts = Date.parse(cleaned + ' UTC')
  return new Date(ts - 8 * 60 * 60 * 1000)
}

function transformToPayouts(transfers: RawTransferEntry[]): Payout[] {
  const grouped = new Map<string, RawTransferEntry[]>()
  for (const t of transfers) {
    const key = t.externalPayoutReference
    if (!grouped.has(key)) grouped.set(key, [])
    grouped.get(key)!.push(t)
  }
  const payouts: Payout[] = []
  let payoutCounter = 10001

  // Special-cases — sprinkle in status variety so the detail page can
  // demonstrate all the timeline / pill states.
  const specialStatuses = new Map<string, PayoutStatus>([
    ['BML-20260505-TXN-00179', 'partially_submitted'],
  ])

  Array.from(grouped.entries()).forEach(([bankRef, group]) => {
    const first = group[0]
    const grossAmount = round2(group.reduce((s, t) => s + t.grossAmount, 0))
    const totalDeductions = round2(
      group.reduce((s, t) => s + t.totalDeductions, 0),
    )
    const netAmount = round2(group.reduce((s, t) => s + t.netAmount, 0))
    const rawStatus = first.payoutStatus.toLowerCase() as PayoutStatus
    const status: PayoutStatus = specialStatuses.get(bankRef) || rawStatus

    const transfers: PayoutTransfer[] = group.map((t, idx) => {
      let transferStatus: TransferStatus = 'submitted'
      if (status === 'partially_submitted' && idx === group.length - 1)
        transferStatus = 'failed'
      if (status === 'pending') transferStatus = 'pending'
      return {
        transferId: `pt_${String(idx + 1).padStart(3, '0')}`,
        status: transferStatus,
        grossAmount: t.grossAmount,
        totalDeductions: t.totalDeductions,
        netAmount: t.netAmount,
        bankReference: `${displayBankRef(t.externalPayoutReference)}-${String(idx + 1).padStart(2, '0')}`,
        description: t.description,
      }
    })

    payouts.push({
      payoutId: `po_${payoutCounter++}`,
      merchantEntity: first.merchantEntity,
      status,
      effectiveDate: first.payoutEffectiveDate,
      effectiveDateRaw: parseEffectiveDate(first.payoutEffectiveDate),
      currency: first.currency,
      grossAmount,
      totalDeductions,
      netAmount,
      destinationAccount: first.destinationAccount,
      transfers,
    })
  })

  payouts.sort(
    (a, b) => b.effectiveDateRaw.getTime() - a.effectiveDateRaw.getTime(),
  )
  return payouts
}

const round2 = (n: number) => Math.round(n * 100) / 100

// ── Public API ───────────────────────────────────────────────────────────

let cachedPayouts: Payout[] | null = null

export function getAllPayouts(): Payout[] {
  if (!cachedPayouts) cachedPayouts = transformToPayouts(rawTransfers)
  return cachedPayouts
}

export function getPayoutById(id: string): Payout | undefined {
  return getAllPayouts().find((p) => p.payoutId === id)
}

/**
 * Derive a display status from PayoutStatus. A 'submitted' payout always
 * maps to 'paid' regardless of age — bank delivery time isn't observable
 * from our side, so we surface a single honest "Sent" state instead of
 * inventing an "in transit" prediction.
 */
export function getDisplayStatus(payout: Payout): DisplayStatus {
  if (payout.status === 'submitted') return 'paid'
  if (payout.status === 'pending') return 'scheduled'
  return payout.status
}

// ── Bank account details ─────────────────────────────────────────────────

export interface PayoutAccountDetails {
  bank: string
  bankShort: string
  last4: string
  type: string
  created: Date
  accountHolder: string
  origin: { country: string; flag: string }
  fingerprint: string
  routingNumber: string
  bankAccountId: string
}

export function getAccountDetails(payout: Payout): PayoutAccountDetails {
  return {
    bank: BRAND.name,
    bankShort: BRAND.shortCode,
    last4: '4521',
    type: 'Bank account',
    created: new Date('2024-03-15T02:23:00Z'),
    accountHolder: payout.merchantEntity.toUpperCase(),
    origin: { country: 'Malaysia', flag: '🇲🇾' },
    fingerprint: 'RbnICHwZlZtd37Nt',
    routingNumber: BRAND.bank.swift,
    bankAccountId: `ba_1${payout.payoutId
      .replace(/[^a-z0-9]/gi, '')
      .padEnd(20, '0')
      .slice(0, 20)}`,
  }
}

// ── Breakdown / deductions ───────────────────────────────────────────────
// A real payout is rarely just "gross − processing fees". It can have
// payout-level holdbacks the merchant needs to understand:
//   • Minimum retained balance (acquirer keeps a floor in Available)
//   • Previously unpaid fees / receivables (rolled forward from last cycle)
//   • Negative balance carry-over (when Available went below zero)
//   • Payout fee (per-payout bank transfer cost)
// These show as distinct line items so the merchant can reconcile.

export type AdjustmentKind =
  | 'minimum_balance_retained'
  | 'unpaid_fees_carryover'
  | 'negative_balance_carryover'
  | 'payout_fee'

export interface AdjustmentLine {
  kind: AdjustmentKind
  label: string
  reference: string
  description: string
  amount: number // signed; deductions are negative
}

export interface BreakdownRow {
  count: number
  gross: number
  fees: number
  total: number
}

export interface PayoutBreakdown {
  charges: BreakdownRow
  refunds: BreakdownRow
  chargebacks: BreakdownRow
  adjustments: AdjustmentLine[]
  /** Sum of all rows + adjustments — should equal the payout net. */
  net: number
}

export function getPayoutBreakdown(payout: Payout): PayoutBreakdown {
  const seed = payout.payoutId
    .split('')
    .reduce((acc, c) => acc + c.charCodeAt(0), 0)
  const rng = (i: number) => {
    const x = Math.sin(seed * 7 + i * 13) * 10000
    return x - Math.floor(x)
  }

  const gross = payout.grossAmount
  // Charges make up the bulk of every payout.
  const chargeCount = 12 + Math.floor(rng(1) * 20)
  const chargeGross = round2(gross * (0.96 + rng(2) * 0.03))
  const chargeFees = round2(chargeGross * 0.029)

  const hasRefunds = rng(3) > 0.4
  const refundCount = hasRefunds ? 1 + Math.floor(rng(4) * 3) : 0
  const refundGross = hasRefunds ? -round2(50 + rng(5) * 2000) : 0

  const hasChargebacks = rng(6) > 0.88
  const chargebackCount = hasChargebacks ? 1 : 0
  const chargebackGross = hasChargebacks ? -round2(100 + rng(7) * 500) : 0
  const chargebackFees = hasChargebacks ? -30 : 0

  // Payout-level holdbacks. Pattern keyed off the numeric suffix of po_xxxxx
  // so different payouts demonstrate different deduction shapes.
  const adjustments: AdjustmentLine[] = []
  const idNum = parseInt(payout.payoutId.replace(/^po_/, ''), 10) || 0
  const mod = idNum % 10

  if (mod === 0 || mod === 3 || mod === 6) {
    adjustments.push({
      kind: 'minimum_balance_retained',
      label: 'Minimum balance retained',
      reference: 'policy_min_balance',
      description: 'Held in Available per merchant policy',
      amount: -round2(500 + rng(10) * 2500),
    })
  }
  if (mod === 7) {
    adjustments.push({
      kind: 'unpaid_fees_carryover',
      label: 'Previously unpaid fees',
      reference: 'carryover_fees',
      description: 'Outstanding fees rolled over from prior cycle',
      amount: -round2(20 + rng(11) * 300),
    })
  }
  if (mod === 4) {
    adjustments.push({
      kind: 'negative_balance_carryover',
      label: 'Negative balance brought forward',
      reference: 'carryover_balance',
      description: 'Available balance went below zero last cycle',
      amount: -round2(100 + rng(12) * 900),
    })
  }
  adjustments.push({
    kind: 'payout_fee',
    label: 'Payout fee',
    reference: 'payout_fee',
    description: `${BRAND.name} transfer fee`,
    amount: -2.5,
  })

  const adjustmentsTotal = adjustments.reduce((s, a) => s + a.amount, 0)
  const net = round2(
    chargeGross -
      chargeFees +
      refundGross +
      chargebackGross +
      chargebackFees +
      adjustmentsTotal,
  )

  return {
    charges: {
      count: chargeCount,
      gross: chargeGross,
      fees: chargeFees,
      total: round2(chargeGross - chargeFees),
    },
    refunds: {
      count: refundCount,
      gross: refundGross,
      fees: 0,
      total: refundGross,
    },
    chargebacks: {
      count: chargebackCount,
      gross: chargebackGross,
      fees: chargebackFees,
      total: round2(chargebackGross + chargebackFees),
    },
    adjustments,
    net,
  }
}

// ── Timeline ─────────────────────────────────────────────────────────────

export interface TimelineStep {
  /** Headline label, e.g. "Funds arrived" */
  label: string
  /** When this step happened (or is expected to). null for non-temporal. */
  date: Date | null
  /** True if this step has already occurred. */
  done: boolean
  /** Optional extra detail line shown beneath the label. */
  detail?: string
  /** Optional range label (e.g. for the in-transit window). */
  rangeLabel?: string
}

const ONE_DAY = 24 * 60 * 60 * 1000

// MVT is UTC+8. To snap a Date to "00:01 MVT" on the calendar day that
// `eff` falls into (in MVT) minus 2 days: shift into MVT, floor to the day
// boundary, subtract 2 days, add 1 minute, shift back to UTC.
const MVT_OFFSET_MS = 8 * 60 * 60 * 1000
function initiatedAtMVTMidnight(eff: Date): Date {
  const mytTs = eff.getTime() + MVT_OFFSET_MS
  const dayMs = 24 * 60 * 60 * 1000
  const mytDayStart = Math.floor(mytTs / dayMs) * dayMs
  const initiatedMytTs = mytDayStart - 2 * dayMs + 60 * 1000 // 00:01 MVT
  return new Date(initiatedMytTs - MVT_OFFSET_MS)
}

export function getTimeline(payout: Payout): TimelineStep[] {
  const eff = payout.effectiveDateRaw
  const initiated = initiatedAtMVTMidnight(eff)

  const display = getDisplayStatus(payout)

  // Bank-side delivery (in-transit window, "funds arriving") is intentionally
  // omitted — we can't reliably predict when CIMB credits the destination
  // account, so we only show the events we can speak to: the payout's
  // initiation, and any terminal status the payout itself reaches.
  switch (display) {
    case 'paid':
      return [
        {
          label: 'Payout initiated automatically',
          date: initiated,
          done: true,
        },
      ]
    case 'scheduled':
      return [
        {
          label: 'Payout scheduled',
          date: eff,
          done: false,
          detail:
            'This payout will be created automatically on the scheduled date and submitted to your bank.',
        },
      ]
    case 'partially_submitted':
      return [
        {
          label: 'Partially submitted',
          date: eff,
          done: true,
          detail:
            'Some transfers in this payout failed. Review the transfers list and contact Support if you need a re-attempt.',
        },
        {
          label: 'Payout initiated automatically',
          date: initiated,
          done: true,
        },
      ]
    case 'failed':
      return [
        {
          label: 'Payout failed',
          date: eff,
          done: true,
          detail: `Submission to ${BRAND.name} failed. Funds remain in your Available balance and the payout will retry on the next cycle.`,
        },
        {
          label: 'Payout initiated automatically',
          date: initiated,
          done: true,
        },
      ]
    case 'cancelled':
      return [
        { label: 'Payout cancelled', date: eff, done: true },
        {
          label: 'Payout initiated automatically',
          date: initiated,
          done: true,
        },
      ]
  }
}

function shortDate(d: Date): string {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/**
 * The moment the payout left our system (auto-initiated). For sent
 * payouts this is the date/time we'd surface in the page header, since
 * bank-side delivery isn't observable.
 */
export function getInitiatedAt(payout: Payout): Date {
  return initiatedAtMVTMidnight(payout.effectiveDateRaw)
}

// ── Trace ID ─────────────────────────────────────────────────────────────

/**
 * Bank-side trace ID merchants reconcile against their statements. Format
 * mimics Malaysian interbank reference style.
 */
export function getPayoutTraceId(payout: Payout): string {
  const eff = payout.effectiveDateRaw
  const yyyymmdd =
    eff.getFullYear().toString() +
    String(eff.getMonth() + 1).padStart(2, '0') +
    String(eff.getDate()).padStart(2, '0')
  const tail = payout.payoutId.slice(-6).toUpperCase().padStart(6, '0')
  return `MY2ON${yyyymmdd}${tail}`
}
